/* eslint-disable @typescript-eslint/camelcase */
import http from "@/utils/http";
import { API_URL_V2 } from "@/recourses/constants";
import * as authUrl from "@/recourses/urls/auth";

const BASE_API_URL = import.meta.env.REACT_APP_BASE_API_URL;
const AUTH_PHONE_ENDPOINT = "auth/telephone/";
const AUTH_PHONE_VALIDATION_ENDPOINT = "auth/telephone/callback/";
const AUTH_REGISTRATION_ENDPOINT = "auth/telephone/registration/";
const AUTH_CHANGE_PASSWORD_ENDPOINT = "auth/telephone/change-password/";
const AUTH_LOGIN_ENDPOINT = "auth/login/";
const AUTH_VERIFY = "auth/verify/";
const USER_SETTINGS = "settings/";

const createPhoneCode = async ({
  phoneNumber,
  type,
  channel,
  recaptchaToken,
}: IPhoneCodeParams): Promise<IPhoneCodeResponse> => {
  const response = await http.post<{ message: string }>(
    `${BASE_API_URL}api/v1/${AUTH_PHONE_ENDPOINT}`,
    {
      telephone: phoneNumber,
      type,
      channel,
      recaptcha_token: recaptchaToken,
    },
    { isAuth: false }
  );
  return response.data;
};

const validatePhoneCode = async ({
  phoneNumber,
  code,
}: {
  phoneNumber: string;
  code: string;
}): Promise<{
  token: string;
}> => {
  const response = await http.post<{ token: string }>(
    `${BASE_API_URL}api/v1/${AUTH_PHONE_VALIDATION_ENDPOINT}`,
    {
      telephone: phoneNumber,
      code,
    },
    { isAuth: false }
  );
  return response.data;
};

const createUser = async ({
  email,
  password,
  firstName,
  lastName,
  birthday,
  token,
  gender,
  hearAboutUs,
  identification,
  identificationType,
  recaptchaToken,
}: createUserProps): Promise<UserAttr> => {
  const response = await http.post<UserAttr>(
    `${BASE_API_URL}api/v1/${AUTH_REGISTRATION_ENDPOINT}`,
    {
      email: email.toLowerCase(),
      password,
      gender,
      first_name: firstName,
      last_name: lastName,
      dob: birthday,
      hear_about_us_source: hearAboutUs,
      identification,
      identification_type: identificationType,
      recaptcha_token: recaptchaToken,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

const loginUser = async ({
  email,
  password,
  recaptchaToken,
}: ILoginUserParams): Promise<ILoginUserResponse> => {
  const response = await http.post<ILoginUserResponse>(`${BASE_API_URL}api/v1/${AUTH_LOGIN_ENDPOINT}`, {
    email: email.toLowerCase(),
    password,
    recaptcha_token: recaptchaToken,
  });

  return response.data;
};

const changePassword = async ({
  newPassword,
  token,
  recaptchaToken,
}: IIResetPasswordParams): Promise<IIResetPasswordResponse> => {
  const response = await http.put<IIResetPasswordResponse>(
    `${BASE_API_URL}api/v1/${AUTH_CHANGE_PASSWORD_ENDPOINT}`,
    {
      password: newPassword,
      confirm_password: newPassword,
      recaptcha_token: recaptchaToken,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

const setUserSettings = async ({
  hearAboutUs,
  gender,
  dob,
  identification,
  identificationType,
}: setUserSettingsParams): Promise<any> => {
  const response = await http.patch<{
    message: string;
  }>(`${BASE_API_URL}api/v1/${USER_SETTINGS}`, {
    ...(hearAboutUs ? { hear_about_us_source: hearAboutUs } : {}),
    ...(gender ? { gender } : {}),
    ...(dob ? { dob } : {}),
    ...(identification && identificationType ? { identification } : {}),
    ...(identification && identificationType ? { identification_type: identificationType } : {}),
  });

  return response.data;
};

const getUserSettings = async ({ token }: any): Promise<{ token: string; user: Omit<UserAttr, "token"> }> => {
  const response = await http.post<{
    token: string;
    user: Omit<UserAttr, "token">;
  }>(`${BASE_API_URL}api/v1/${AUTH_VERIFY}`, {
    token,
  });

  return response.data;
};

const getTokenPlus = async (password: string) => {
  return await http.post(`${API_URL_V2}${authUrl.TOKEN_PLUS}`, {
    password,
  });
};
export const changePasswordV2 = async ({
  currentPassword,
  newPassword,
  recaptchaToken,
}: IchangePasswordV2Params) => {
  await http.post(`${API_URL_V2}${authUrl.AUTH_CHANGE_PASSWORD_ENDPOINT_V2}`, {
    old_password: currentPassword,
    new_password: newPassword,
    recaptcha_token: recaptchaToken,
  });
};

export const changePhoneV2 = async ({ newPhone, channel, plusToken }: changePhoneV2Props) => {
  return await http.post(
    `${API_URL_V2}${authUrl.AUTH_PHONE_CHANGE}`,
    {
      new_phone: newPhone,
      channel,
    },
    {
      headers: {
        "Plus-Token": plusToken,
      },
    }
  );
};
export const verifyPlusToken = async (plusToken: string) => {
  return await http.post(
    `${API_URL_V2}${authUrl.AUTH_VERIFY_PLUS_TOKEN}`,
    {
      plusToken,
    },
    {
      headers: {
        "Plus-Token": plusToken,
      },
    }
  );
};
export const verifyPhoneChange = async ({ phone, code, plusToken }: verifyPhoneChangeProps) => {
  return await http.post(
    `${API_URL_V2}${authUrl.AUTH_VERIFY_CODE}`,
    {
      new_phone: phone,
      verification_code: code,
    },
    {
      headers: {
        "Plus-Token": plusToken,
      },
    }
  );
};

export const verifyTokenJWT = async () => {
  const token = window.localStorage.getItem("userToken");
  if (!token) {
    throw new Error("Token not found");
  }

  const response = await http.post(
    authUrl.verifyTokenJWT,
    {
      token: JSON.parse(token),
    },
    { isAuth: false }
  );
  return response.data;
};

export {
  getUserSettings,
  createPhoneCode,
  validatePhoneCode,
  createUser,
  loginUser,
  changePassword,
  setUserSettings,
  getTokenPlus,
};
